import React, { useState, useEffect, useMemo } from "react";
import { getClubEvents } from "../../services/eventServices";
import { useUser } from "../../contexts/UserContext";
import { getRoleFromRoleId } from "../../utils/roleHelper";
import useScrollToTop from "../../hooks/useScrollToTop";
import Button from "../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";
import CreateEventModal from "./CreateEventModal";
import EventCard from "./EventCard";
import InfoBubble from "../../components/InfoBubble/InfoBubble";
import PageHeader from "../../components/Layout/PageHeader";

const ClubEventsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Filtres
  const [searchTitle, setSearchTitle] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [showOnlyUpcoming, setShowOnlyUpcoming] = useState(true);
  const [showOnlyMine, setShowOnlyMine] = useState(false);

  // Tri
  const [sortBy, setSortBy] = useState("start"); // 'start' | 'title' | 'type'
  const [sortDir, setSortDir] = useState("asc"); // 'asc' | 'desc'

  const categories = [
    "Eveil",
    "Mini-Poussin(e)",
    "Poussin(e)",
    "Benjamin(e)",
    "Minime",
    "Cadet(te)",
    "Junior",
    "Sénior",
    "Vétéran",
  ];

  const types = ["Cours commun Club", "Stage Club", "Tournoi Interclubs"];

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const response = await getClubEvents();
        setEvents(response || []);
      } catch (error) {
        console.error("Failed to fetch events:", error);
        toast.error("Erreur lors du chargement des événements");
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      const response = await getClubEvents();
      setEvents(response || []);
      toast.success("Liste des événements actualisée");
    } catch (error) {
      console.error("Failed to refresh events:", error);
      toast.error("Erreur lors de l’actualisation des événements");
    } finally {
      setLoading(false);
    }
  };

  const handleAddEvent = () => {
    setIsCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    handleRefresh();
  };

  const filteredAndSortedEvents = useMemo(() => {
    const now = new Date();
    let list = [...events];

    // 🔍 Filtre titre
    const q = searchTitle.trim().toLowerCase();
    if (q) {
      list = list.filter((event) =>
        (event.titre || "").toLowerCase().includes(q)
      );
    }

    // 🎯 Filtre catégorie
    if (selectedCategory) {
      list = list.filter((event) =>
        (event.cate_event || "").includes(selectedCategory)
      );
    }

    // 🏷️ Filtre type
    if (selectedType) {
      list = list.filter((event) => event.type_event === selectedType);
    }

    // ⏱️ Filtre à venir / passés
    if (showOnlyUpcoming) {
      list = list.filter((event) => {
        const endDate = event.end || event.start;
        const d = new Date(endDate);
        if (isNaN(d)) return true; // si pas de date exploitable, on garde
        return d >= now;
      });
    }

    // 👤 Filtre "Mes événements"
    if (showOnlyMine && user) {
      list = list.filter((event) => event.user_id === user.userId);
    }

    // 🔁 Tri
    list.sort((a, b) => {
      if (sortBy === "start") {
        const da = new Date(a.start);
        const db = new Date(b.start);
        const va = isNaN(da) ? 0 : da.getTime();
        const vb = isNaN(db) ? 0 : db.getTime();
        return va - vb;
      }

      if (sortBy === "title") {
        const ta = (a.titre || "").toLowerCase();
        const tb = (b.titre || "").toLowerCase();
        if (ta < tb) return -1;
        if (ta > tb) return 1;
        return 0;
      }

      if (sortBy === "type") {
        const ta = (a.type_event || "").toLowerCase();
        const tb = (b.type_event || "").toLowerCase();
        if (ta < tb) return -1;
        if (ta > tb) return 1;
        return 0;
      }

      return 0;
    });

    if (sortDir === "desc") {
      list.reverse();
    }

    return list;
  }, [
    events,
    searchTitle,
    selectedCategory,
    selectedType,
    showOnlyUpcoming,
    showOnlyMine,
    sortBy,
    sortDir,
    user,
  ]);

  if (!user) {
    return <div className="mt-40 text-center text-slate-600">Chargement…</div>;
  }

  const canCreate =
    userRole === "Administrateur" ||
    userRole === "Bureau" ||
    userRole === "Responsables_clubs";

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      {/* En-tête unifiée */}
      <PageHeader
        eyebrow="Responsables clubs"
        title="Proposer cours communs, stages et tournois interclubs"
        subtitle="Créez et gérez les événements de votre club qui seront proposés aux autres clubs du département. Ils seront soumis à validation avant d’apparaître dans le calendrier départemental."
        align="center"
        size="md"
      />

      <div className="w-full max-w-6xl mx-auto space-y-4">
        <InfoBubble title="Bienvenue sur la page des propositions d’événements clubs">
          <p>
            Sur cette page, les responsables de clubs peuvent proposer des cours
            communs, stages ou tournois interclubs aux clubs girondins et à
            leurs licenciés. Prenez soin de bien renseigner les catégories
            d’âge, le lieu et les horaires. L’événement sera validé par le CTF
            ou la commission animation avant d’être publié.
          </p>
        </InfoBubble>

        {/* Barre d’actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          {canCreate && (
            <Button
              text="Proposer un événement"
              onClick={handleAddEvent}
              className="w-full sm:w-auto"
            />
          )}
          <button
            type="button"
            onClick={handleRefresh}
            className="text-xs sm:text-sm inline-flex items-center justify-center rounded-full border border-slate-300 dark:border-slate-600 px-3 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
          >
            Actualiser la liste
          </button>
        </div>

        {/* Filtres & tris */}
        <div className="rounded-xl bg-white/80 dark:bg-slate-900/80 border border-slate-200/70 dark:border-slate-700/70 shadow-sm p-3 sm:p-4 space-y-3">
          {/* Ligne 1 : recherche titre */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Rechercher par titre
              </label>
              <input
                type="text"
                placeholder="Ex : Stage d’arbitrage, Tournoi interclubs..."
                value={searchTitle}
                onChange={(e) => setSearchTitle(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>
          </div>

          {/* Ligne 2 : catégories & type */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Catégorie
              </label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <option value="">Toutes les catégories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Type d’événement
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <option value="">Tous les types</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ligne 3 : toggles + tri */}
          <div className="flex flex-col md:flex-row gap-2 md:gap-3 md:items-end">
            <div className="flex-1 flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => setShowOnlyUpcoming((v) => !v)}
                className={`flex-1 text-xs sm:text-[0.75rem] inline-flex items-center justify-center rounded-full border px-3 py-1.5 ${
                  showOnlyUpcoming
                    ? "bg-primary text-white border-primary"
                    : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                }`}
              >
                {showOnlyUpcoming
                  ? "Uniquement événements à venir"
                  : "Inclure les événements passés"}
              </button>

              <button
                type="button"
                onClick={() => setShowOnlyMine((v) => !v)}
                className={`flex-1 text-xs sm:text-[0.75rem] inline-flex items-center justify-center rounded-full border px-3 py-1.5 ${
                  showOnlyMine
                    ? "bg-slate-800 text-white border-slate-800"
                    : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
                }`}
              >
                {showOnlyMine ? "Uniquement mes événements" : "Tous les auteurs"}
              </button>
            </div>

            <div className="flex-1 flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                  Trier par
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
                >
                  <option value="start">Date de début</option>
                  <option value="title">Titre</option>
                  <option value="type">Type d’événement</option>
                </select>
              </div>

              <div className="w-32">
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                  Ordre
                </label>
                <select
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
                >
                  <option value="asc">Croissant</option>
                  <option value="desc">Décroissant</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Liste des events */}
        {loading ? (
          <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300">
            Chargement des événements…
          </div>
        ) : filteredAndSortedEvents.length === 0 ? (
          <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-300">
            Aucun événement ne correspond à vos filtres.
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {filteredAndSortedEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                fetchEvents={handleRefresh}
              />
            ))}
          </div>
        )}
      </div>

      <CreateEventModal
        isOpen={isCreateModalOpen}
        onClose={closeCreateModal}
        userId={user.userId}
        fetchEvents={handleRefresh}
      />
      <ToastContainer />
    </div>
  );
};

export default ClubEventsPage;
